import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import imgLogo from "../img/imgLogo.png";
import { CONTACT, MAILTO } from "../data/contactInfo";

/* ===== Helpers scroll robustes ===== */
function getScrollY() {
  const htmlY = document.documentElement ? document.documentElement.scrollTop : 0;
  const bodyY = document.body ? document.body.scrollTop : 0;
  const winY = typeof window !== "undefined" ? window.scrollY || 0 : 0;
  return Math.max(htmlY, bodyY, winY);
}

// Optimisé: UN SEUL event listener au lieu de 6
function addScrollListener(handler) {
  const opts = { passive: true };
  window.addEventListener("scroll", handler, opts);
  return () => {
    window.removeEventListener("scroll", handler, opts);
  };
}

const HeaderMenu = React.memo(() => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isHidden, setIsHidden] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("accueil");
  const [indicatorStyle, setIndicatorStyle] = useState({});
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isServicesOpenedByClick, setIsServicesOpenedByClick] = useState(false);

  const lastScrollY = useRef(0);
  const ticking = useRef(false);
  const navRef = useRef(null);
  const servicesRef = useRef(null);
  const location = useLocation();

  // Utiliser refs au lieu de state dans deps pour éviter la boucle
  const scrolledRef = useRef(false);
  const hiddenRef = useRef(false);
  const isMobileMenuOpenRef = useRef(false);

  // Sync refs avec state
  useEffect(() => {
    scrolledRef.current = isScrolled;
    hiddenRef.current = isHidden;
    isMobileMenuOpenRef.current = isMobileMenuOpen;
  }, [isScrolled, isHidden, isMobileMenuOpen]);

  /* ===== Scroll optimisé ===== */
  const computeScrollState = useCallback(() => {
    const y = getScrollY();
    const isDesktop = window.innerWidth > 768;
    const isContactPage = location.pathname === '/contact';

    const SCROLLED_AT = 100;
    const SCROLLED_AT_RETURN = 50;
    const HIDE_AFTER = 400;
    const DEADZONE = isDesktop ? 80 : 100; // Plus de deadzone sur mobile pour moins de calculs

    const delta = y - lastScrollY.current;
    const bigEnough = Math.abs(delta) >= DEADZONE;

    // Hystérésis pour scrolled
    const scrolled = scrolledRef.current
      ? y > SCROLLED_AT_RETURN
      : y > SCROLLED_AT;

    let hidden = false;

    // Logique de hiding (fonctionne mobile + desktop)
    if (isContactPage) {
      // Pas de hiding sur page contact
      hidden = false;
    } else if (!isMobileMenuOpenRef.current) {
      if (y <= HIDE_AFTER) {
        // Toujours visible en haut de page
        hidden = false;
      } else if (bigEnough) {
        // Cache si scroll down, montre si scroll up
        hidden = delta > 0;
        lastScrollY.current = y;
      } else {
        // Garde l'état actuel si mouvement trop petit
        hidden = hiddenRef.current;
      }
    }

    // Batch state updates
    if (scrolledRef.current !== scrolled || hiddenRef.current !== hidden) {
      if (scrolledRef.current !== scrolled) setIsScrolled(scrolled);
      if (hiddenRef.current !== hidden) setIsHidden(hidden);
    }

    ticking.current = false;
  }, [location.pathname]);

  const onAnyScroll = useCallback(() => {
    if (ticking.current) return;
    ticking.current = true;
    requestAnimationFrame(computeScrollState);
  }, [computeScrollState]);

  useEffect(() => {
    lastScrollY.current = getScrollY();
    computeScrollState();
    const remove = addScrollListener(onAnyScroll);
    return remove;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onAnyScroll]);

  /* ===== Lock body quand menu mobile ouvert ===== */
  useEffect(() => {
    if (isMobileMenuOpen) {
      const scrollY = getScrollY();
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = "100%";
      document.body.style.overflow = "hidden";
      document.documentElement.style.overflow = "hidden";
    } else {
      const top = document.body.style.top;
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
      if (top) {
        const y = parseInt(top, 10) * -1;
        window.scrollTo(0, y);
      }
    }
    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  /* ===== Reset au changement de page SEULEMENT ===== */
  useEffect(() => {
    setIsHidden(false);
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
    setIsServicesOpenedByClick(false);
    setIsMobileServicesOpen(false);
    lastScrollY.current = 0;
  }, [location.pathname]);

  /* ===== Init scroll séparé ===== */
  useEffect(() => {
    requestAnimationFrame(() => computeScrollState());
  }, [computeScrollState]);

  /* ===== Anti-transform de garde ===== */
  useEffect(() => {
    document.documentElement.classList.add("no-transform-scroll-root");
    document.body.classList.add("no-transform-scroll-root");

    const cls = "mobile-menu-open";
    if (isMobileMenuOpen) {
      document.documentElement.classList.add(cls);
      document.body.classList.add(cls);
    } else {
      document.documentElement.classList.remove(cls);
      document.body.classList.remove(cls);
    }
    return () => {
      document.documentElement.classList.remove("no-transform-scroll-root");
      document.body.classList.remove("no-transform-scroll-root");
      document.documentElement.classList.remove(cls);
      document.body.classList.remove(cls);
    };
  }, [isMobileMenuOpen]);

  /* ===== Gestion clic extérieur dropdown desktop ===== */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (servicesRef.current && !servicesRef.current.contains(event.target)) {
        setIsServicesOpen(false);
        setIsServicesOpenedByClick(false);
      }
    };

    if (isServicesOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isServicesOpen]);

  /* ===== Indicateur (desktop) ===== */
  const updateIndicator = useCallback(() => {
    if (!navRef.current) return;

    // Skip sur mobile pour performance
    if (window.innerWidth <= 768) return;

    const navList = navRef.current.querySelector(".nav-list");
    const activeLink = navRef.current.querySelector(".nav-link.active");
    if (!navList || !activeLink) {
      setIndicatorStyle((prev) => (prev.opacity === 0 ? prev : { ...prev, opacity: 0 }));
      return;
    }
    const lr = navList.getBoundingClientRect();
    const ar = activeLink.getBoundingClientRect();
    setIndicatorStyle({
      left: `${ar.left - lr.left}px`,
      width: `${ar.width}px`,
      opacity: 1,
    });
  }, []);

  useEffect(() => {
    const t = setTimeout(updateIndicator, 50);
    return () => clearTimeout(t);
  }, [location.pathname, activeSection, updateIndicator]);

  useEffect(() => {
    let resizeTimeout;
    const onResize = () => {
      // Debounce resize pour performance
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(updateIndicator, 150);
    };
    window.addEventListener("resize", onResize, { passive: true });
    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener("resize", onResize);
    };
  }, [updateIndicator]);

  /* ===== Menu items ===== */
  const menuItems = useMemo(
    () => [
      { id: "accueil", label: "Accueil", path: "/" },
      { id: "realisations", label: "Séries", path: "/realisations" },
      { id: "services", label: "Services", path: null, hasDropdown: true },
      // Boutique masquée temporairement : { id: "boutique", label: "Boutique", path: "/print" },
      { id: "a-propos", label: "À propos", path: "/a-propos" },
    ],
    []
  );

  const servicesLinks = useMemo(
    () => [
      { id: "service-portraits", label: "Portrait Signature", path: "/services/portraits" },
      { id: "service-entreprise", label: "Image d'Entreprise", path: "/services/entreprise" },
      { id: "service-evenementiel", label: "Evenement Signature", path: "/services/evenementiel" },
    ],
    []
  );

  const isServiceActive = useMemo(
    () => servicesLinks.some((service) => location.pathname.startsWith(service.path)),
    [servicesLinks, location.pathname]
  );

  useEffect(() => {
    if (!isMobileMenuOpen) {
      setIsMobileServicesOpen(false);
    } else if (isServiceActive) {
      setIsMobileServicesOpen(true);
    }
  }, [isMobileMenuOpen, isServiceActive]);

  /* ===== Active section ===== */
  useEffect(() => {
    const p = location.pathname;
    if (p === "/a-propos") setActiveSection("a-propos");
    else if (p === "/realisations" || p.includes("/realisation")) setActiveSection("realisations");
    else if (p === "/contact") setActiveSection("contact");
    else setActiveSection("accueil");
  }, [location.pathname]);

  /* ===== Actions ===== */
  const toggleMobileMenu = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsMobileMenuOpen((v) => !v);
  }, []);

  const handleNavClick = useCallback(() => {
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
  }, []);

  return (
    <>
      <a href="#main-content" className="skip-to-content">
        Aller au contenu principal
      </a>
      <header
        className={`header ${isScrolled ? "scrolled" : ""} ${isHidden ? "hidden" : ""} ${
          isMobileMenuOpen ? "menu-open" : ""
        }`}
        role="banner"
      >
        <div className="header-container">
          {/* Logo */}
          <div className="logo-container">
            {!isMobileMenuOpen && (
              <Link to="/" className="logo" onClick={handleNavClick} aria-label="Accueil - Tanguy Perroit Photographe">
                <span className="logo-icon">
                  <img src={imgLogo} alt="Logo Tanguy Perroit" style={{ width: 49 }} />
                </span>
                <span className="logo-text">
                  <span className="logo-name">Tanguy Perroit</span>
                  <span className="logo-tagline">Photographe</span>
                </span>
              </Link>
            )}
          </div>

          {/* Nav desktop */}
          <nav className="nav-desktop" ref={navRef} aria-label="Navigation principale">
            <ul className="nav-list">
              {menuItems.map((item) => {
                // Si c'est le dropdown Prestations
                if (item.hasDropdown) {
                  return (
                    <li
                      key={item.id}
                      className="nav-item nav-item--dropdown"
                      ref={servicesRef}
                      onMouseEnter={() => {
                        if (!isServicesOpenedByClick) {
                          setIsServicesOpen(true);
                        }
                      }}
                      onMouseLeave={() => {
                        if (!isServicesOpenedByClick) {
                          setIsServicesOpen(false);
                        }
                      }}
                    >
                      <button
                        className={`nav-link nav-link--dropdown ${isServiceActive ? "active" : ""}`}
                        onClick={() => {
                          setIsServicesOpen(!isServicesOpen);
                          setIsServicesOpenedByClick(!isServicesOpen);
                        }}
                        aria-expanded={isServicesOpen}
                        aria-haspopup="true"
                      >
                        {item.label}
                        <span className={`dropdown-arrow ${isServicesOpen ? "open" : ""}`}>▼</span>
                      </button>

                      <div className={`dropdown-menu ${isServicesOpen ? "open" : ""}`}>
                        {servicesLinks.map((service) => (
                          <NavLink
                            key={service.id}
                            to={service.path}
                            className={({ isActive }) =>
                              `dropdown-link ${isActive ? "active" : ""}`
                            }
                            onClick={() => {
                              setIsServicesOpen(false);
                              setIsServicesOpenedByClick(false);
                              handleNavClick();
                            }}
                          >
                            {service.label}
                          </NavLink>
                        ))}
                      </div>
                    </li>
                  );
                }

                // Liens normaux
                return (
                  <li key={item.id} className="nav-item">
                    <NavLink
                      to={item.path}
                      className={({ isActive }) =>
                        `nav-link ${
                          isActive ||
                          (item.id === "realisations" &&
                            location.pathname.includes("/realisation"))
                            ? "active"
                            : ""
                        }`
                      }
                      onClick={handleNavClick}
                    >
                      {item.label}
                    </NavLink>
                  </li>
                );
              })}
            </ul>
            <span className="nav-indicator" style={indicatorStyle} />
          </nav>

          {/* Droite header + Burger */}
          <div className="header-right">
            <a
              href={CONTACT.social.instagram}
              target="_blank"
              rel="noopener noreferrer"
              className="instagram-link"
              aria-label="Instagram"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
              </svg>
            </a>

            <div className="header-cta">
              <Link to="/contact" className="cta-button" onClick={handleNavClick}>
                <span>Contact</span>
                <span className="cta-arrow">→</span>
              </Link>
            </div>

            <button
              type="button"
              className={`hamburger ${isMobileMenuOpen ? "active" : ""}`}
              onClick={toggleMobileMenu}
              aria-label="Ouvrir le menu"
              aria-expanded={isMobileMenuOpen ? "true" : "false"}
              aria-controls="mobile-menu"
            >
              <span className="hamburger-line"></span>
              <span className="hamburger-line"></span>
              <span className="hamburger-line"></span>
            </button>
          </div>
        </div>
      </header>

      {/* Overlay mobile */}
      <div
        id="mobile-menu"
        className={`mobile-menu ${isMobileMenuOpen ? "open" : ""}`}
        onClick={() => setIsMobileMenuOpen(false)}
      >
        <div className="mobile-menu-glass-container" onClick={(e) => e.stopPropagation()}>
          <button
            type="button"
            className="mobile-menu-close"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-label="Fermer le menu"
          >
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
            <span className="hamburger-line"></span>
          </button>

          <div className="mobile-logo-section">
            <img src={imgLogo} alt="logo" style={{ width: 40, marginBottom: 16 }} />
            <div className="mobile-logo-name">Tanguy Perroit</div>
            <div className="mobile-logo-title">Photographe</div>
          </div>

          <nav className="mobile-nav-section">
  {/* Menu principal */}
  <div className={`mobile-nav-panel mobile-nav-panel--main ${isMobileServicesOpen ? 'slide-out' : ''}`}>
    <ul className="mobile-nav-list">
      {/* 01 - Accueil */}
      <li className="mobile-nav-item" style={{ animationDelay: '0s' }}>
        <NavLink
          to="/"
          className={({ isActive }) => `mobile-nav-link ${isActive ? "active" : ""}`}
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <span className="mobile-nav-number">01</span>
          <span className="mobile-nav-text">Accueil</span>
        </NavLink>
      </li>

      {/* 02 - Séries */}
      <li className="mobile-nav-item" style={{ animationDelay: '0.1s' }}>
        <NavLink
          to="/realisations"
          className={({ isActive }) =>
            `mobile-nav-link ${
              isActive || location.pathname.includes("/realisation") ? "active" : ""
            }`
          }
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <span className="mobile-nav-number">02</span>
          <span className="mobile-nav-text">Séries</span>
        </NavLink>
      </li>

      {/* 03 - Services (dropdown) */}
      <li className="mobile-nav-item" style={{ animationDelay: '0.2s' }}>
        <button
          type="button"
          className={`mobile-nav-link ${isServiceActive ? "active" : ""}`}
          onClick={() => setIsMobileServicesOpen(true)}
        >
          <span className="mobile-nav-number">03</span>
          <span className="mobile-nav-text">Services</span>
          <span className="mobile-nav-arrow">→</span>
        </button>
      </li>

      {/* Boutique masquée temporairement */}

      {/* 04 - À propos */}
      <li className="mobile-nav-item" style={{ animationDelay: '0.3s' }}>
        <NavLink
          to="/a-propos"
          className={({ isActive }) => `mobile-nav-link ${isActive ? "active" : ""}`}
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <span className="mobile-nav-number">04</span>
          <span className="mobile-nav-text">À propos</span>
        </NavLink>
      </li>

      {/* 05 - Contact */}
      <li
        key="contact"
        className="mobile-nav-item"
        style={{ animationDelay: '0.5s' }}
      >
        <NavLink
          to="/contact"
          className={({ isActive }) => `mobile-nav-link ${isActive ? "active" : ""}`}
          onClick={() => setIsMobileMenuOpen(false)}
        >
          <span className="mobile-nav-number">05</span>
          <span className="mobile-nav-text">Contact</span>
        </NavLink>
      </li>
    </ul>
  </div>

  {/* Panneau des services - slide depuis la droite */}
  <div className={`mobile-nav-panel mobile-nav-panel--services ${isMobileServicesOpen ? 'slide-in' : ''}`}>
    <button
      type="button"
      className="mobile-nav-back"
      onClick={() => setIsMobileServicesOpen(false)}
    >
      <span className="back-arrow">←</span>
      <span>Retour</span>
    </button>

    <div className="mobile-nav-panel-title">Services</div>

    <ul className="mobile-nav-list">
      {servicesLinks.map((service, index) => (
        <li
          key={service.id}
          className="mobile-nav-item"
          style={{ animationDelay: `${index * 0.1}s` }}
        >
          <NavLink
            to={service.path}
            className={({ isActive }) =>
              `mobile-nav-link ${isActive ? "active" : ""}`
            }
            onClick={() => {
              setIsMobileServicesOpen(false);
              setIsMobileMenuOpen(false);
            }}
          >
            <span className="mobile-nav-text">{service.label}</span>
          </NavLink>
        </li>
      ))}
    </ul>
  </div>
</nav>

          <div className="mobile-cta-section">
            <Link to="/contact" className="mobile-cta-button" onClick={() => setIsMobileMenuOpen(false)}>
              <span>Me contacter</span>
              <span className="mobile-cta-arrow">→</span>
            </Link>
          </div>

          <div className="mobile-contact-section">
            <div className="mobile-contact-info">
              <a href={CONTACT.phone.href} className="mobile-contact-item">
                <svg className="contact-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                </svg>
                <span>{CONTACT.phone.display}</span>
              </a>
              <a href={MAILTO} className="mobile-contact-item">
                <svg className="contact-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
                <span>{CONTACT.email}</span>
              </a>
            </div>
          </div>

          <div className="mobile-social-section">
            <div className="mobile-social-links">
              <a
                href={CONTACT.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="mobile-social-link"
                aria-label="Instagram"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                  <rect x="2" y="2" width="20" height="20" rx="5" ry="5"></rect>
                  <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"></path>
                  <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"></line>
                </svg>
              </a>
              <a
                href={CONTACT.social.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="mobile-social-link"
                aria-label="LinkedIn"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
});

export default HeaderMenu;